// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useCallback, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './CardOwners.module.css'
import MemberName from 'components/community/MemberName'
import { useOwnerTrack, useOwnerViewBeacon } from 'lib/owner-notes/analytics'
import { useNoteCarousel } from 'lib/owner-notes/note-carousel'
import { ownersDeepLink } from 'lib/owner-notes/band-copy'
import {
  noteRead,
  ownersHeadline,
  ownersSubline,
  readSentence,
  type CardOwnersView,
} from 'lib/owner-notes/card-owners'
import type { OwnerCardNote } from 'lib/dealhunt/types'
import { communityThreadHref } from 'lib/community/writer-handoff'
import { publicConfig } from 'config'
import ReviewReplyRow from './ReviewReplyRow'

interface CardOwnersProps {
  owners: CardOwnersView | null
  /** The card's battery spec — the "vs … h claimed" half of the hours line. */
  claimedHours: number | null
  /** The card's PDP; the link lands on its Owners section. */
  pdpHref: string | null
  nodeId: string
  productName: string
  /** Where this block sits, for `notes_click` — a category card (the default) or a guide pick.
   *  Tapping a note's own sentence keeps the `_note` suffix either way, its "Reply →" `_reply`. */
  location?: 'card' | 'pick'
}

/**
 * The card's Owners block (contract #115, carousel #118): on every card whose product has a
 * published note, server-rendered from the v3 card payload. The aggregate the reader gets
 * without touching anything — "x of n would buy again", photo-verified, the hours reality
 * check — sits above a track of one slide per owner: their sentence, one read of theirs as a
 * sentence, their handle. The slides are `top_notes` in stored order (sentiment-blind, #54) and
 * the track is native scroll-snap, so the browser's scroll-direction lock arbitrates a sideways
 * page-through against a vertical drag on the grid — no gesture handler of ours to get that
 * wrong.
 *
 * One note renders as a bare slide: no track, no dots. Most products have one, and a control
 * on a card with nothing to page through was the fair half of #116's objection.
 *
 * Nothing at all without a note: no "0 owners", no empty box. The owner-note ask is never on a
 * card: it is the reference strip's (`DeclaredStrip`).
 */
const CardOwners: React.VFC<CardOwnersProps> = ({
  owners,
  claimedHours,
  pdpHref,
  nodeId,
  productName,
  location = 'card',
}) => {
  const trackOwner = useOwnerTrack({ nodeId, name: productName })
  const trailingSlash = publicConfig('useTrailingSlash')
  // Views (#138, §19.1): the visible slide, half on screen for a second, is a read of that note
  // — on a category card, a best-list pick or a versus block alike. One page-wide beacon.
  const rootRef = useRef<HTMLElement>(null)
  useOwnerViewBeacon(rootRef, { enabled: true, deps: [owners?.notes.length ?? 0] })
  const [expanded, setExpanded] = useState(false)
  // The expanded note is the active one, so paging away must collapse: otherwise the track
  // keeps the tall note's height and the card grows around a slide nobody is reading.
  const collapse = useCallback(() => setExpanded(false), [])
  const { trackRef, active, goTo } = useNoteCarousel(owners?.notes.length ?? 0, collapse)

  if (!owners) {
    return null
  }
  const { notes } = owners
  const sub = ownersSubline(owners, claimedHours)
  const many = notes.length > 1
  // The headline's mark reads the aggregate only — the card payload has no per-owner buy-again.
  // All yes earns the tick; all no gets a GREY cross, never red: one owner is not a verdict.
  // A split (or unknown) gets no mark — the words already say it.
  const ba = owners.buyAgain
  let mark: 'markYes' | 'markNo' | null = null
  if (ba && ba.total > 0 && ba.yes === ba.total) {
    mark = 'markYes'
  } else if (ba && ba.total > 0 && ba.yes === 0) {
    mark = 'markNo'
  }

  const onNoteToggle = (): void => {
    setExpanded(prev => {
      if (!prev) {
        trackOwner('notes_click', { location: `${location}_note`, item_count: owners.count })
      }
      return !prev
    })
  }

  const slide = (note: OwnerCardNote, i: number): JSX.Element => {
    const read = noteRead(owners, note)
    const open = expanded && i === active
    return (
      <figure
        key={note.id}
        className={styles.slide}
        data-note-id={note.id}
        role={many ? 'group' : undefined}
        aria-label={many ? `Owner ${i + 1} of ${notes.length}` : undefined}
      >
        {/* Tap the sentence to read all of it — a 280-character note is eight lines in this
            column, so the card clamps to two and grows only when the reader asks it to. */}
        <button
          type="button"
          className={clsx(styles.noteButton, { [styles.noteOpen]: open })}
          aria-expanded={open}
          onClick={onNoteToggle}
        >
          <blockquote className={styles.noteText}>{`“${note.note}”`}</blockquote>
        </button>
        {read && (
          // A sentence in the owner's voice, not a red/green grade: it is one owner's
          // expectation, never the product's score, and a bold "worse" read as a bad product
          // beside a happy note. Which read still follows question order, never polarity.
          <p className={styles.read}>{readSentence(read)}</p>
        )}
        <figcaption className={styles.by}>
          {'— '}
          {/* DealHunt #218: `Name @handle` when the member shows a name, `@handle` otherwise. */}
          {note.display_name ? (
            <MemberName name={note.display_name} handle={note.handle} />
          ) : (
            `@${note.handle}`
          )}
          {note.photo_status === 'verified' && (
            <span className={styles.verified}>{' · Photo'}</span>
          )}
        </figcaption>
        {/* Reply only: the card payload carries no `helpful_count` (DealHunt's cardNotes keeps
            id, handle, sentence, reads) — a Helpful control here would invent a 0. */}
        <ReviewReplyRow
          handle={note.handle}
          thread={communityThreadHref(pdpHref, note.id, trailingSlash)}
          onReply={() =>
            trackOwner('notes_click', { location: `${location}_reply`, item_count: owners.count })
          }
        />
      </figure>
    )
  }

  return (
    <section className={styles.root} ref={rootRef} aria-label={`Owners of the ${productName}`}>
      <header className={styles.head}>
        <span className={styles.heading}>{'Owners'}</span>
        {pdpHref && (
          <a
            href={ownersDeepLink(pdpHref)}
            className={styles.link}
            onClick={() => trackOwner('notes_click', { location, item_count: owners.count })}
          >
            {owners.count === 1 ? 'Read the owner review ›' : `All ${owners.count} ›`}
          </a>
        )}
      </header>

      {/* Pinned above the track: a reader who never pages still gets the verdict and the
          sample size behind it. The count stays on its own muted line rather than riding the
          headline — at this width the headline wraps, and an inline " · " orphans. */}
      <p className={styles.headline}>
        {mark && (
          <span className={clsx(styles.mark, styles[mark])} aria-hidden="true">
            {mark === 'markYes' ? '✓' : '✕'}
          </span>
        )}
        {ownersHeadline(owners)}
      </p>
      {sub && <p className={styles.sub}>{sub}</p>}

      {many ? (
        <>
          <div className={styles.track} ref={trackRef}>
            {notes.map(slide)}
          </div>
          <div className={styles.dots} role="tablist" aria-label="Owner">
            {notes.map((note, i) => (
              <button
                key={note.id}
                type="button"
                role="tab"
                className={styles.dot}
                aria-selected={i === active}
                aria-label={`Owner ${i + 1} of ${notes.length}`}
                onClick={() => goTo(i)}
              >
                <span className={styles.dotMark} aria-hidden="true" />
              </button>
            ))}
          </div>
        </>
      ) : (
        notes.map(slide)
      )}
    </section>
  )
}

export default CardOwners
